import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import "./healthcare-capital-hero-video.css";

const VIDEO_SRC = `${process.env.PUBLIC_URL || ""}/videos/healthcare-capital-hero.mp4`;
const POSTER_SRC = `${process.env.PUBLIC_URL || ""}/videos/healthcare-capital-hero-poster.jpg`;

function shouldDeferHeavyVideo() {
  if (typeof window === "undefined") return true;
  try {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true;
    if (window.matchMedia("(max-width: 900px)").matches) return true;
    if (navigator.connection?.saveData) return true;
  } catch {
    /* ignore */
  }
  return false;
}

function PosterOnly({ className, onPlayClick }) {
  return (
    <div className={`nh-hero-video ${className}`.trim()} aria-hidden={!onPlayClick}>
      <div
        className="nh-hero-video__poster nh-hero-video__poster--motion"
        style={{ backgroundImage: `url(${POSTER_SRC})` }}
      />
      <div className="nh-hero-video__vignette" />
      {onPlayClick ? (
        <button type="button" className="nh-hero-video__play" onClick={onPlayClick} aria-label="Play background video">
          Play video
        </button>
      ) : null}
    </div>
  );
}

export default function HealthcareCapitalHeroVideo({ className = "" }) {
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const preferLight = useMemo(() => shouldDeferHeavyVideo(), []);
  const [loadVideo, setLoadVideo] = useState(!preferLight);

  const tryPlay = useCallback(() => {
    const el = videoRef.current;
    if (!el || failed) return;
    el.muted = true;
    el.defaultMuted = true;
    el.setAttribute("muted", "");
    const promise = el.play();
    if (promise?.catch) {
      promise.catch(() => {
        const resume = () => {
          el.play().catch(() => {});
          document.removeEventListener("pointerdown", resume);
          document.removeEventListener("keydown", resume);
        };
        document.addEventListener("pointerdown", resume, { once: true });
        document.addEventListener("keydown", resume, { once: true });
      });
    }
  }, [failed]);

  useEffect(() => {
    if (!loadVideo || failed) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;

    const onReady = () => tryPlay();

    el.addEventListener("loadeddata", onReady);
    el.addEventListener("canplay", onReady);

    if (el.readyState >= 2) {
      tryPlay();
    }

    let attempts = 0;
    const retryId = window.setInterval(() => {
      attempts += 1;
      if (attempts > 24 || !el.paused) {
        window.clearInterval(retryId);
        return;
      }
      tryPlay();
    }, 400);

    const observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              if (entries.some((e) => e.isIntersecting)) tryPlay();
            },
            { threshold: 0.1 }
          )
        : null;
    observer?.observe(el);

    return () => {
      el.removeEventListener("loadeddata", onReady);
      el.removeEventListener("canplay", onReady);
      window.clearInterval(retryId);
      observer?.disconnect();
    };
  }, [failed, loadVideo, tryPlay]);

  if (failed || !loadVideo) {
    return (
      <PosterOnly
        className={className}
        onPlayClick={
          preferLight && !failed && !loadVideo
            ? () => setLoadVideo(true)
            : undefined
        }
      />
    );
  }

  return (
    <div className={`nh-hero-video ${className}`.trim()} aria-hidden>
      <video
        ref={videoRef}
        className="nh-hero-video__media"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={POSTER_SRC}
        onError={() => setFailed(true)}
        onLoadedData={tryPlay}
      >
        <source src={VIDEO_SRC} type="video/mp4" />
      </video>
      <div className="nh-hero-video__vignette" />
    </div>
  );
}
